import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { isDemoMode, demoEnrollmentsStore, seedDemoEnrollmentsIfEmpty } from '../utils/demo';

const Enrollments: React.FC = () => {
  const demo = isDemoMode();
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState(() => demo ? demoEnrollmentsStore.readAll() : []);
  const [enrolledIds, setEnrolledIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (demo) {
      seedDemoEnrollmentsIfEmpty();
      setRows(demoEnrollmentsStore.readAll());
    }
  }, [demo]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      r.firstName.toLowerCase().includes(q) ||
      r.lastName.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q) ||
      (r.employer || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const handleEnroll = (id: string) => {
    setEnrolledIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id); // Toggle off if already enrolled
      } else {
        newSet.add(id); // Toggle on if not enrolled
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-2 py-8" style={{ minWidth: '1000px' }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="bg-brand-gradient rounded-brand p-6 mb-4 text-white shadow-brand">
        <h1 className="text-3xl font-bold mb-2">Enrollments</h1>
        <p className="text-sky-100">Searchable list of individuals associated with an Employer</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.2 }} className="bg-white rounded-brand shadow-brand overflow-x-auto" style={{ minWidth: '950px' }}>
        <div className="p-4 border-b">
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" /></svg>
            </div>
            <input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-seafoam focus:border-transparent w-full" placeholder="Search by name, email, employer..." />
          </div>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Effective Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employer</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Application Link</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filtered.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">{r.effectiveDate}</td>
                <td className="px-6 py-4 whitespace-nowrap">{r.firstName}</td>
                <td className="px-6 py-4 whitespace-nowrap">{r.lastName}</td>
                <td className="px-6 py-4 whitespace-nowrap">{r.employer}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <a href={r.healthSherpaUrl || '#'} target="_blank" rel="noreferrer" className="bg-primary-500 hover:bg-primary-600 text-white px-3 py-1 rounded-md text-sm">Open</a>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <motion.button
                    onClick={() => handleEnroll(r.id)}
                    className={`${
                      enrolledIds.has(r.id)
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-seafoam hover:bg-seafoam-600'
                    } text-white px-3 py-1 rounded-md text-sm transition-colors duration-300`}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.1 }}
                  >
                    {enrolledIds.has(r.id) ? 'Enrolled ✓' : 'Enroll'}
                  </motion.button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </motion.div>
    </div>
  );
};

export default Enrollments;